/* =========================================================================
   VYKA self-owned currency engine  (replaces Nova / Booster)
   -------------------------------------------------------------------------
   - Reads rates from window.VYKA_FX (server-rendered from shop metafield).
   - Converts every Shopify `.money` element site-wide (lossless: keeps the
     original AED markup so AED restores exactly and re-conversion is clean).
   - Converts the embedded diamond widget (#diamond-proxy-root) via overlays
     (React-safe — never fights the widget for its own text nodes).
   - Self-heals: if rates are missing/stale it refreshes from public CDNs and
     caches them; if everything fails it still shows AED.
   - Checkout is always AED. Converted prices are indicative display only.
   Contract: window.VYKA_CONFIG, window.VYKA_FX, window.VYKA_STATE (set in
   snippets/vyka-currency-head.liquid). Public API: window.VYKA.
   ========================================================================= */
(function () {
  'use strict';

  var CFG = window.VYKA_CONFIG || {};
  var BASE = CFG.base || 'AED';
  var LIST = CFG.currencies || [];
  var SOURCES = CFG.sources || [];
  var LS_CUR = CFG.lsKey || 'vyka_currency';
  var COOKIE = CFG.cookie || 'vyka_currency';
  var LS_RATES = 'vyka_rates';
  var LS_RATES_TS = 'vyka_rates_ts';
  var RATES_TTL = 36 * 60 * 60 * 1000;      // refresh rates older than 36h
  var REVEAL_FAILSAFE = 1500;               // never keep prices hidden longer

  // ---- lookups from config -------------------------------------------------
  var SYM = {}, DEC = {}, KNOWN = {};
  for (var i = 0; i < LIST.length; i++) {
    SYM[LIST[i].code] = LIST[i].symbol;
    DEC[LIST[i].code] = LIST[i].dec || 0;
    KNOWN[LIST[i].code] = true;
  }

  // ---- state ---------------------------------------------------------------
  var state = {
    active: (window.VYKA_STATE && window.VYKA_STATE.active) || BASE,
    fx: window.VYKA_FX || { base: BASE, rates: {} }
  };

  // ---- helpers -------------------------------------------------------------
  function rates() { return (state.fx && state.fx.rates) || {}; }

  /* vyka-served-currency-v1: Shopify can render a market in a currency other than the AED
     pivot this rate table is built on (e.g. the Saudi market set to SAR). Detect what Shopify
     actually rendered and normalise it back to AED before converting — otherwise a SAR figure
     would be read as dirhams and every derived price would be wrong. */
  function servedCurrency() {
    try {
      var a = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      return (a && (KNOWN[a] || rates()[a])) ? a : BASE;
    } catch (e) { return BASE; }
  }
  function toPivot(amount) {
    if (amount == null || !isFinite(amount)) return null;
    var s = servedCurrency();
    if (s === BASE) return amount;
    var r = rates()[s];
    return (r && isFinite(r) && r > 0) ? (amount / r) : null;
  }

  function convert(aed, code) {
    if (code === BASE) return null;
    var r = rates()[code];
    if (!r || !isFinite(r) || aed == null || !isFinite(aed)) return null;
    return aed * r;
  }

  // Round UP so the indicative price never shows LOWER than the AED actually
  // charged at checkout (a customer must never be billed more than they saw).
  // Nearest 10 for every currency; nearest 1000 for PKR (its values are huge).
  var ROUND = { PKR: 1000 };
  function roundUp(v, code) {
    var step = ROUND[code] || 10;
    return Math.ceil(v / step) * step;
  }

  function format(aed, code) {
    var v = convert(aed, code);
    if (v == null) return null;
    v = roundUp(v, code);                                  // always rounds up to a clean figure
    var num = v.toLocaleString('en-US', { maximumFractionDigits: 0 });
    var sym = SYM[code] || (code + ' ');
    var sep = /[A-Za-z]$/.test(sym) ? ' ' : '';
    return sym + sep + num;
  }

  // Parse an AED amount out of theme-rendered money text ("AED 1,200", "1,200 AED").
  function parseAmount(text) {
    var m = (text || '').match(/\d[\d.,]*/);
    if (!m) return null;
    var v = parseFloat(m[0].replace(/,/g, ''));
    return isFinite(v) ? v : null;
  }

  function cookieSet(code) {
    try {
      var d = new Date(); d.setTime(d.getTime() + 365 * 864e5);
      document.cookie = COOKIE + '=' + code + ';path=/;expires=' + d.toUTCString() + ';SameSite=Lax';
    } catch (e) {}
  }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }

  function injectStyles() {
    if (document.getElementById('vyka-currency-styles')) return;
    var s = document.createElement('style');
    s.id = 'vyka-currency-styles';
    s.textContent =
      '.vyka-overlay{display:inline;font:inherit;color:inherit;letter-spacing:inherit;white-space:nowrap}' +
      '.vyka-aed-hidden{font-size:0!important;line-height:0!important;color:transparent!important}' +
      '#vyka-currency-banner{display:none;background:#FAFAFA;color:#1c1b19;border:1px solid rgba(168,130,76,.30);' +
      'border-radius:6px;padding:11px 16px;margin:0 0 18px;font-size:13px;line-height:1.5;text-align:center}' +
      '#vyka-currency-banner.vyka-banner-show{display:block}' +
      '#vyka-currency-banner strong{font-weight:600}' +
      '@media (max-width:749px){#vyka-currency-banner{font-size:12px;padding:9px 12px}}';
    (document.head || document.documentElement).appendChild(s);
  }

  // =========================================================================
  // Shopify `.money` conversion (server-rendered prices everywhere but the embedded widget)
  // =========================================================================
  var MONEY_SEL = '.money';

  function processMoney(el, code) {
    if (!el || el.closest('[data-vyka-skip]') || el.closest('#diamond-proxy-root')) return;

    var html = el.innerHTML;
    var sig = el.getAttribute('data-vyka-sig');

    // If the theme re-rendered this node (morph / variant / cart update), our
    // signature won't match — recapture it as a fresh AED original.
    if (sig === null || html !== sig) {
      el.setAttribute('data-vyka-orig', html);
      var aed = toPivot(parseAmount(el.textContent));   // normalise to the AED pivot
      el.setAttribute('data-vyka-aed', aed == null ? '' : String(aed));
    }

    var aedVal = parseFloat(el.getAttribute('data-vyka-aed'));
    if (code === BASE || !isFinite(aedVal) || aedVal <= 0) {
      /* vyka-base-format-v1: render the BASE currency through the same shape as every other
         currency ("AED 4,860") instead of restoring the theme's raw markup ("4,860.00 AED").
         Without this, selecting AED looked broken beside "SAR 4,970" / "$1,330".
         Cents are preserved when a price genuinely has them; the raw markup remains the
         fallback for anything we could not parse into a usable amount. */
      if (code === BASE && isFinite(aedVal) && aedVal > 0) {
        var _hasCents = (Math.round(aedVal * 100) % 100) !== 0;
        var _num = aedVal.toLocaleString('en-US', {
          minimumFractionDigits: _hasCents ? 2 : 0,
          maximumFractionDigits: _hasCents ? 2 : 0
        });
        var _sym = SYM[BASE] || (BASE + ' ');
        var _out = _sym + (/[A-Za-z]$/.test(_sym) ? ' ' : '') + _num;
        if (el.textContent !== _out) el.innerHTML = _out;
        el.setAttribute('data-vyka-sig', el.innerHTML);
        el.setAttribute('data-vyka-cur', BASE);
        return;
      }
      // Restore the untouched AED markup.
      var orig = el.getAttribute('data-vyka-orig');
      if (orig != null && el.innerHTML !== orig) el.innerHTML = orig;
      el.setAttribute('data-vyka-sig', el.innerHTML);
      el.setAttribute('data-vyka-cur', BASE);
      return;
    }

    /* If the shopper picked the very currency Shopify rendered, restore Shopify's own
       markup — it is authoritative and must not be re-derived through our rates. */
    var _served = servedCurrency();
    if (code === _served) {
      var _so = el.getAttribute('data-vyka-orig');
      if (_so != null && el.innerHTML !== _so) el.innerHTML = _so;
      el.setAttribute('data-vyka-sig', el.innerHTML);
      el.setAttribute('data-vyka-cur', _served);
      return;
    }
    var out = format(aedVal, code);
    if (out == null) { el.setAttribute('data-vyka-cur', BASE); return; }
    if (el.textContent !== out) el.innerHTML = out;
    el.setAttribute('data-vyka-sig', el.innerHTML);
    el.setAttribute('data-vyka-cur', code);
  }

  function applyMoney(code, root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll(MONEY_SEL);
    for (var i = 0; i < nodes.length; i++) processMoney(nodes[i], code);
  }

  // =========================================================================
  // Embedded widget conversion (#diamond-proxy-root) — overlay, React-safe
  // =========================================================================
  var NIV_ID = 'diamond-proxy-root';
  var nivPairs = [];
  var AED_RE = /^([\d,]+(?:\.\d+)?)\s*AED$/;

  function nivClear() {
    for (var i = 0; i < nivPairs.length; i++) {
      var p = nivPairs[i];
      if (p.overlay && p.overlay.parentNode) p.overlay.parentNode.removeChild(p.overlay);
      if (p.el) { p.el.classList.remove('vyka-aed-hidden'); p.el.removeAttribute('aria-hidden'); }
    }
    nivPairs = [];
    var left = document.querySelectorAll('#' + NIV_ID + ' .vyka-aed-hidden');
    for (var k = 0; k < left.length; k++) { left[k].classList.remove('vyka-aed-hidden'); left[k].removeAttribute('aria-hidden'); }
  }

  function nivApply(code) {
    nivClear();
    nivBanner(code);
    if (code === BASE) return;
    var root = document.getElementById(NIV_ID);
    if (!root) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false), node;
    while ((node = walker.nextNode())) {
      var m = (node.textContent || '').trim().match(AED_RE);
      if (!m) continue;
      var aed = parseFloat(m[1].replace(/,/g, ''));
      if (!isFinite(aed) || aed < 100) continue;
      var el = node.parentElement;
      if (!el || !el.parentNode || (el.classList && el.classList.contains('vyka-overlay'))) continue;
      var out = format(aed, code);
      if (out == null) continue;
      var ov = document.createElement('span');
      ov.className = 'vyka-overlay';
      ov.setAttribute('aria-label', out + ' (charged in AED)');
      ov.textContent = out;
      el.classList.add('vyka-aed-hidden');
      el.setAttribute('aria-hidden', 'true');
      el.parentNode.insertBefore(ov, el.nextSibling);
      nivPairs.push({ overlay: ov, el: el });
    }
  }

  // Indicative-pricing banner above the widget results.
  function nivBanner(code) {
    var root = document.getElementById(NIV_ID);
    var existing = document.getElementById('vyka-currency-banner');
    if (!root || code === BASE) { if (existing) existing.classList.remove('vyka-banner-show'); return; }
    var b = existing;
    if (!b) {
      b = document.createElement('div');
      b.id = 'vyka-currency-banner';
      b.setAttribute('role', 'note');
    }
    if (root.parentNode && b.parentNode !== root.parentNode) root.parentNode.insertBefore(b, root);
    var label = (SYM[code] || code).trim();
    b.innerHTML = 'Prices shown in <strong>' + label + ' (' + code + ')</strong>. Orders are charged in <strong>AED</strong> at checkout — your bank may apply a small FX adjustment.';
    b.classList.add('vyka-banner-show');
  }

  // =========================================================================
  // Orchestration
  // =========================================================================
  var revealed = false;
  function reveal() {
    if (revealed) return;
    revealed = true;
    document.documentElement.removeAttribute('data-vyka-cloak');
  }

  var moneyTimer = null;
  function scheduleMoney() {
    clearTimeout(moneyTimer);
    moneyTimer = setTimeout(function () { applyMoney(state.active); }, 80);
  }
  var nivTimer = null;
  function scheduleNiv() {
    clearTimeout(nivTimer);
    nivTimer = setTimeout(function () { nivApply(state.active); }, 150);
  }

  function applyAll() {
    applyMoney(state.active);
    nivApply(state.active);
    reveal();
  }

  function setCurrency(code, soft) {
    if (!KNOWN[code]) return;
    if (code !== state.active) {
      state.active = code;
      if (!soft) cookieSet(code);   // soft = geo auto-detect: cache in LS, but don't pin a cookie
      lsSet(LS_CUR, code);
      if (window.VYKA_STATE) window.VYKA_STATE.active = code;
    }
    if (code !== BASE) document.documentElement.setAttribute('data-vyka-active', code);
    else document.documentElement.removeAttribute('data-vyka-active');
    applyAll();
    try { document.dispatchEvent(new CustomEvent('vyka:currency-change', { detail: { currency: code } })); } catch (e) {}
  }

  // ---- rate freshness + CDN self-heal -------------------------------------
  function fresh(fx) { return fx && fx.rates && fx.rates[BASE] && fx.ts && (Date.now() - fx.ts) < RATES_TTL; }

  function fetchJSON(url) {
    return fetch(url, { mode: 'cors', cache: 'no-store' }).then(function (r) {
      if (!r.ok) throw new Error(url + ' ' + r.status); return r.json();
    });
  }
  function normalizeCDN(j) {
    var src = j[BASE.toLowerCase()] || j.rates || null;   // jsDelivr/cloudflare vs er-api
    if (!src) return null;
    var out = {}, low = {};
    for (var k in src) low[k.toLowerCase()] = src[k];
    for (var c in KNOWN) out[c] = c === BASE ? 1 : low[c.toLowerCase()];
    return out;
  }
  function refreshFromCDN(idx) {
    idx = idx || 0;
    if (idx >= SOURCES.length) return;
    fetchJSON(SOURCES[idx]).then(function (j) {
      var r = normalizeCDN(j);
      if (!r || !r[BASE]) throw new Error('bad payload');
      state.fx = { base: BASE, ts: Date.now(), source: 'cdn', rates: r };
      lsSet(LS_RATES, JSON.stringify(r));
      lsSet(LS_RATES_TS, String(Date.now()));
      applyAll();
    }).catch(function () { refreshFromCDN(idx + 1); });
  }

  function ensureRates() {
    if (fresh(state.fx)) return;                  // server metafield is fresh — done
    // Try a still-valid client cache before hitting the network.
    var ts = parseInt(lsGet(LS_RATES_TS) || '0', 10);
    if (ts && (Date.now() - ts) < RATES_TTL) {
      try {
        var cached = JSON.parse(lsGet(LS_RATES) || 'null');
        if (cached && cached[BASE]) { state.fx = { base: BASE, ts: ts, source: 'cache', rates: cached }; }
      } catch (e) {}
    }
    refreshFromCDN(0);                            // refresh in background regardless
  }

  // ---- first-visit IP geolocation (keyless, async, pure fallback) ----------
  var GEO_SOURCES = CFG.geoSources || [];
  var COUNTRY = CFG.countryMap || {};
  function detectGeo(cb) {
    var i = 0;
    (function next() {
      if (i >= GEO_SOURCES.length) { cb(null); return; }       // every provider exhausted
      fetchJSON(GEO_SOURCES[i++]).then(function (j) {
        // Prefer country_code; some providers (ipwho.is) put the full name in `country`.
        var raw = ((j && (j.country_code || j.country)) || '').toString().toUpperCase();
        var cc = /^[A-Z]{2}$/.test(raw) ? raw : '';
        if (!cc) { next(); return; }                           // responded but no usable ISO code -> try next provider
        cb(COUNTRY[cc] || null);                               // valid code (null = supported response, unmapped country)
      }).catch(next);                                          // network error -> try next provider
    })();
  }

  // ---- observers -----------------------------------------------------------
  function startObservers() {
    // Global: catch new .money nodes (variant change, cart drawer, quick-add, pagination).
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes && muts[i].addedNodes.length) { scheduleMoney(); break; }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // The widget: its React tree re-renders on every search/filter.
    var root = document.getElementById(NIV_ID);
    if (root) {
      new MutationObserver(function () { scheduleNiv(); }).observe(root, { childList: true, subtree: true });
    } else {
      // Widget mounts late — watch for it.
      var w = new MutationObserver(function () {
        var r = document.getElementById(NIV_ID);
        if (r) { w.disconnect(); new MutationObserver(function () { scheduleNiv(); }).observe(r, { childList: true, subtree: true }); scheduleNiv(); }
      });
      w.observe(document.body, { childList: true, subtree: true });
    }

    // Theme editor / section reloads.
    document.addEventListener('shopify:section:load', applyAll);
  }

  // ---- public API ----------------------------------------------------------
  window.VYKA = {
    config: CFG,
    getCurrency: function () { return state.active; },
    setCurrency: setCurrency,
    format: format,
    convert: convert,
    refreshRates: function () { refreshFromCDN(0); },
    ready: false
  };

  function boot() {
    injectStyles();
    ensureRates();
    if (window.VYKA_STATE && window.VYKA_STATE.geoPending) {
      // First visit, no stored/URL signal: prices are cloaked; detect country by IP,
      // then set currency + reveal. 900ms failsafe so prices never stay hidden.
      detectGeo(function (cur) { setCurrency(cur || state.active, true); });
      setTimeout(function () { if (!revealed) setCurrency(state.active, true); }, 900);
    } else {
      setCurrency(state.active);   // first paint conversion + reveal
    }
    startObservers();
    setTimeout(reveal, REVEAL_FAILSAFE);
    window.VYKA.ready = true;
    try { document.dispatchEvent(new CustomEvent('vyka:ready', { detail: { currency: state.active } })); } catch (e) {}
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
